<script setup>
import { computed, ref, watch } from "vue";
import SearchableInput from "@/components/SearchableInput.vue";

const props = defineProps({
  groups: { type: Array, required: true },
  run: { type: Object, default: null },
  products: { type: Array, default: () => [] },
  actions: { type: Object, required: true },
  saveStates: { type: Object, required: true },
  savingIds: { type: Set, required: true },
  initialId: { type: Number, default: null },
  refreshingMatches: Boolean,
  readinessFilter: { type: String, default: "all" },
  metrics: { type: Object, default: null },
});
const selectedId = ref(props.initialId);
const search = ref("");
const sourceOpen = ref(false);
const sourceRotation = ref(0);
const confirming = ref(false);
const acknowledged = ref(false);
const startedAt = ref(Date.now());
const expandedShift = ref(null);
const expandAll = ref(false);
const queue = computed(() =>
  props.groups.filter(
    (group) =>
      (props.readinessFilter === "all" ||
        (props.readinessFilter === "needs_review"
          ? group.issues.length > 0
          : !group.issues.length &&
            !["imported", "excluded"].includes(group.record.status))) &&
      `${group.edit.claimant_name} ${group.record.source_file_name} ${group.record.source_date}`
        .toLowerCase()
        .includes(search.value.toLowerCase()),
  ),
);
const selected = computed(
  () =>
    props.groups.find((group) => group.record.id === selectedId.value) ||
    queue.value[0],
);
const readOnly = computed(
  () =>
    props.run?.status !== "draft" ||
    ["imported", "excluded"].includes(selected.value?.record.status),
);
const leads = computed(
  () => selected.value?.rows.filter((row) => row.entry.is_shift_lead) || [],
);
const sourceIsPreviewable = computed(() =>
  /\.(pdf|png|jpe?g|gif|webp)$/i.test(
    selected.value?.record.source_file_name || "",
  ),
);
const sourceIsImage = computed(() =>
  /\.(png|jpe?g|gif|webp)$/i.test(
    selected.value?.record.source_file_name || "",
  ),
);
const rotateSource = (degrees) => {
  sourceRotation.value = (sourceRotation.value + degrees + 360) % 360;
};
watch(
  () => selected.value?.record.id,
  (id) => {
    if (id) selectedId.value = id;
    acknowledged.value = false;
    startedAt.value = Date.now();
    expandedShift.value = null;
    expandAll.value = false;
    sourceRotation.value = 0;
    const group = props.groups.find((item) => item.record.id === id);
    const firstLead = group?.rows.find((row) => row.entry.is_shift_lead);
    if (
      firstLead &&
      props.run?.status === "draft" &&
      !["imported", "excluded"].includes(group.record.status)
    ) {
      props.actions.focus?.(firstLead);
    }
  },
  { immediate: true },
);
watch([search, () => props.readinessFilter], () => {
  selectedId.value = queue.value[0]?.record.id || null;
});
watch(
  () => props.run?.id,
  () => {
    selectedId.value = null;
    search.value = "";
  },
);
watch(
  () => props.initialId,
  (id) => {
    if (id) {
      selectedId.value = id;
      search.value = "";
    }
  },
);
// An acknowledgement applies only to the values currently visible to the reviewer.
watch(
  () => selected.value && JSON.stringify(selected.value.edit),
  () => {
    acknowledged.value = false;
  },
);
const select = (group) => {
  selectedId.value = group.record.id;
};
const busy = computed(
  () => confirming.value || props.savingIds.has(selected.value?.record.id),
);
const childrenFor = (row) =>
  selected.value.rows.filter(
    (other) =>
      !other.entry.is_shift_lead &&
      other.entry.shift_group_key === row.entry.shift_group_key,
  );
const reviewersFor = (group) => {
  const reviewers = new Map();
  (group?.rows || []).forEach((row) => {
    actionsPresence(row).forEach((person) => {
      reviewers.set(String(person.user_id), person);
    });
  });
  return [...reviewers.values()];
};
const actionsPresence = (row) =>
  props.actions.presence(row.record.id, row.entry.sequence) || [];
const reviewerInitials = (name) =>
  String(name || "?")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");
const reviewerStyle = (userId) => ({
  backgroundColor: `hsl(${(Math.abs(Number(userId) || 0) * 67) % 360} 60% 42%)`,
});
const savedLabel = computed(
  () =>
    ({
      pending: "Unsaved changes…",
      saving: "Saving…",
      saved: "All changes saved",
      error: "Autosave failed — retry saving",
    })[props.saveStates[selected.value?.record.id]] || "Autosave on",
);
const needsAcknowledgement = computed(
  () =>
    selected.value?.issues.length > 0 ||
    selected.value?.record.warnings?.length > 0,
);
const confirmNext = async () => {
  if (!selected.value || busy.value) return;
  const record = selected.value.record;
  const actionable = queue.value.filter(
    (group) => !["imported", "excluded"].includes(group.record.status),
  );
  const index = actionable.findIndex((group) => group.record.id === record.id);
  const next =
    (index < 0 ? actionable[0] : actionable[index + 1] || actionable[index - 1])
      ?.record.id || null;
  confirming.value = true;
  try {
    const ok = await props.actions.confirm(
      record,
      Math.round((Date.now() - startedAt.value) / 1000),
    );
    if (ok) selectedId.value = next;
  } finally {
    confirming.value = false;
  }
};
const suggestions = (row) => row.entry.match_suggestions.slice(0, 3);
const shiftOpen = (row) =>
  expandAll.value || (expandedShift.value ?? leads.value[0]?.key) === row.key;
const toggleShift = (row) => {
  const wasOpen = shiftOpen(row);
  expandAll.value = false;
  expandedShift.value = wasOpen ? "" : row.key;
};
const shiftIssues = (row) =>
  selected.value.issues.filter(
    (issue) => issue.scope === `Row ${row.entry.sequence}`,
  ).length;
</script>

<template>
  <div class="timesheet-review">
    <aside class="review-queue" aria-label="Timesheet review queue">
      <label class="review-label" for="timesheet-queue-search"
        >Find a timesheet</label
      >
      <input
        id="timesheet-queue-search"
        v-model="search"
        class="review-input"
        type="search"
        placeholder="Staff, date or filename"
      />
      <p class="review-muted">{{ queue.length }} timesheets in this view</p>
      <nav class="queue-items" aria-label="Select a timesheet">
        <button
          v-for="group in queue"
          :key="group.key"
          type="button"
          class="queue-item"
          :aria-current="selected?.key === group.key ? 'true' : undefined"
          @click="select(group)"
        >
          <span class="queue-top">
            <span class="queue-name">{{
              group.edit.claimant_name ||
              group.record.source_staff_name ||
              "Unknown claimant"
            }}</span>
            <span
              v-if="reviewersFor(group).length"
              class="queue-reviewers"
              aria-label="Current reviewers"
            >
              <span
                v-for="person in reviewersFor(group)"
                :key="person.user_id"
                class="review-avatar"
                :style="reviewerStyle(person.user_id)"
                :title="`${person.name}${person.is_current_user ? ' (you)' : ''} is working on this timesheet`"
              >
                <img
                  v-if="person.avatar_url"
                  :src="person.avatar_url"
                  :alt="person.name"
                />
                <span v-else>{{ reviewerInitials(person.name) }}</span>
              </span>
            </span>
          </span>
          <span class="review-muted"
            >{{ actions.formatDate(group.record.source_date) }} ·
            {{ group.rows.filter((row) => row.entry.is_shift_lead).length }}
            shifts</span
          >
          <span :class="group.issues.length ? 'review-warning' : 'review-muted'"
            >{{ actions.status(group)
            }}<template v-if="group.issues.length">
              · {{ group.issues.length }} checks</template
            ></span
          >
        </button>
      </nav>
      <details v-if="metrics?.measured_shifts" class="review-evidence">
        <summary>Matching progress</summary>
        <p>
          {{ metrics.automatic }} automatic ·
          {{ metrics.suggestion }} suggestions confirmed ·
          {{ metrics.manual }} manually selected
        </p>
        <p v-if="metrics.average_review_seconds !== null">
          Average review: {{ metrics.average_review_seconds }} seconds across
          {{ metrics.reviewed_timesheets }} timesheets.
        </p>
        <p class="review-muted">
          Measured since this update; older imports are excluded.
        </p>
      </details>
    </aside>

    <section
      v-if="selected"
      :key="selected.key"
      class="review-main"
      aria-label="Selected timesheet"
    >
      <header class="review-heading">
        <div class="min-w-0">
          <div class="review-title-row">
            <h3>{{ selected.edit.claimant_name || "Unknown claimant" }}</h3>
            <span
              v-if="reviewersFor(selected).length"
              class="queue-reviewers"
              aria-label="Currently working on this timesheet"
            >
              <span
                v-for="person in reviewersFor(selected)"
                :key="person.user_id"
                class="review-avatar review-avatar-large"
                :style="reviewerStyle(person.user_id)"
                :title="`${person.name}${person.is_current_user ? ' (you)' : ''} is working on this timesheet`"
              >
                <img
                  v-if="person.avatar_url"
                  :src="person.avatar_url"
                  :alt="person.name"
                />
                <span v-else>{{ reviewerInitials(person.name) }}</span>
              </span>
            </span>
          </div>
          <p class="review-muted break-words">
            {{ actions.title(selected.record) }}
          </p>
        </div>
        <div class="review-buttons">
          <a
            v-if="selected.record.source_view_url"
            :href="selected.record.source_view_url"
            target="_blank"
            rel="noopener"
            class="review-icon"
            title="Open original timesheet in a new tab"
            aria-label="Open original timesheet"
            >↗</a
          >
          <button
            v-if="selected.record.source_view_url && sourceIsPreviewable"
            type="button"
            class="review-icon"
            :class="{ 'is-active': sourceOpen }"
            :aria-expanded="sourceOpen"
            title="Preview original timesheet"
            aria-label="Preview original timesheet"
            @click="sourceOpen = !sourceOpen"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path
                fill="currentColor"
                d="M4 4h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2Zm0 2v12h16V6H4Zm2 10 3.5-4.5 2.5 3 2-2.5 4 5H6Zm9-8.5a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3Z"
              />
            </svg>
          </button>
          <button
            v-if="!readOnly"
            type="button"
            class="btn"
            :disabled="busy || refreshingMatches"
            @click="actions.rematch(selected.record)"
          >
            {{ refreshingMatches ? "Finding matches…" : "Find matches" }}
          </button>
          <details class="review-menu">
            <summary>More actions</summary>
            <div class="review-buttons mt-2">
              <button
                v-if="!readOnly"
                class="btn"
                type="button"
                @click="actions.add(selected.record)"
              >
                Add work row
              </button>
              <button
                v-if="selected.record.status === 'imported'"
                class="btn"
                type="button"
                :disabled="run?.status !== 'draft' || busy"
                @click="actions.reopen(selected.record)"
              >
                Reopen for correction
              </button>
              <button
                v-if="!readOnly"
                class="btn"
                type="button"
                :disabled="busy"
                @click="actions.exclude(selected.record)"
              >
                Exclude timesheet
              </button>
              <button
                v-if="selected.record.source_document_id"
                class="btn"
                type="button"
                :disabled="run?.status !== 'draft' || busy"
                @click="
                  actions.reprocess(
                    { id: selected.record.source_document_id },
                    { verifyWithBestAi: true },
                  )
                "
              >
                Re-read original with AI
              </button>
              <button
                v-if="selected.record.source_document_id"
                class="btn"
                type="button"
                :disabled="run?.status !== 'draft' || busy"
                @click="actions.deleteDocument(selected.record)"
              >
                Delete timesheet
              </button>
            </div>
          </details>
        </div>
      </header>

      <div
        v-if="selected.issues.length && !readOnly"
        class="review-notice"
        role="status"
      >
        <strong>Next: {{ selected.issues[0].message }}</strong>
        <details v-if="selected.issues.length > 1">
          <summary>Show all {{ selected.issues.length }} checks</summary>
          <ul>
            <li
              v-for="issue in selected.issues"
              :key="issue.scope + issue.message"
            >
              {{ issue.scope }}: {{ issue.message }}
            </li>
          </ul>
        </details>
      </div>
      <p
        v-if="selected.record.review_message && !readOnly"
        class="review-muted"
      >
        {{ selected.record.review_message }}
      </p>
      <details v-if="selected.record.warnings?.length" class="review-evidence">
        <summary>
          Extraction notes ({{ selected.record.warnings.length }})
        </summary>
        <p v-for="(warning, index) in selected.record.warnings" :key="index">
          {{ warning.message || warning }}
        </p>
      </details>

      <div class="review-content" :class="{ 'with-source': sourceOpen }">
        <div class="review-editor">
          <div class="review-fields">
            <fieldset
              :disabled="readOnly || confirming"
              class="review-section review-staff"
            >
              <label class="review-label"
                >Staff
                <span class="review-muted">{{
                  selected.edit.staff_id ? "· matched" : "· select claimant"
                }}</span>
                <SearchableInput
                  :model-value="selected.edit.staff_query"
                  :auto-show-results="false"
                  :output="actions.staffOutput"
                  url="/api/staff/search?query="
                  placeholder="Search staff"
                  :clear-on-select="false"
                  input-class="review-input"
                  results-class="!static !max-h-48"
                  @update:modelValue="
                    actions.staffQuery(selected.record, $event)
                  "
                  @selected="actions.staff(selected.record, $event)"
                />
              </label>
            </fieldset>

            <div class="review-heading review-shift-tools">
              <span class="review-muted">{{ leads.length }} shifts</span>
              <button
                type="button"
                class="review-link"
                @click="
                  expandAll = !expandAll;
                  expandedShift = '';
                "
              >
                {{ expandAll ? "Collapse all" : "Expand all" }}
              </button>
            </div>
            <section
              v-for="row in leads"
              :key="row.key"
              class="review-section"
              @focusin="actions.focus(row)"
              @focusout="actions.blur($event, row)"
            >
              <button
                type="button"
                class="shift-summary"
                :aria-expanded="shiftOpen(row)"
                @click="toggleShift(row)"
              >
                <strong
                  >{{ shiftOpen(row) ? "▾" : "▸" }}
                  {{ row.entry.date || "No date" }}</strong
                >
                <span class="shift-job">{{
                  row.entry.job_date_query || "Select job reference"
                }}</span>
                <span
                  >{{ row.entry.work_start_time || "—" }}–{{
                    row.entry.work_finish_time || "—"
                  }}
                  · {{ row.entry.claimed_hours || "—" }} h</span
                >
                <span v-if="shiftIssues(row)" class="review-warning"
                  >{{ shiftIssues(row) }} checks</span
                >
                <span class="review-muted"
                  >{{ childrenFor(row).length }} journeys</span
                >
              </button>
              <fieldset
                v-show="shiftOpen(row)"
                :disabled="readOnly || confirming"
                class="shift-editor"
              >
                <p
                  v-for="person in actions.presence(
                    row.record.id,
                    row.entry.sequence,
                  )"
                  :key="person.user_id"
                  class="review-muted"
                >
                  {{ person.name }} is reviewing this row
                </p>
                <div class="review-grid shift-fields">
                  <label class="review-label shift-date"
                    >Work date<input
                      v-model="row.entry.date"
                      class="review-input"
                      type="date"
                      @change="actions.date(row.entry, row.record)"
                  /></label>
                  <label class="review-label shift-basis"
                    >Payment basis<select
                      v-model="row.entry.payment_basis"
                      class="review-input"
                    >
                      <option value="hourly">Hourly</option>
                      <option value="daily">Daily</option>
                      <option value="item">Item</option>
                      <option value="flat">Flat</option>
                    </select></label
                  >
                  <label class="review-label shift-rate"
                    >Rate code<input
                      v-model="row.entry.agreed_rate"
                      class="review-input"
                  /></label>
                  <div class="shift-matching">
                    <label class="review-label shift-role"
                      >Product / role<select
                        :value="row.entry.product_id"
                        class="review-input"
                        @change="
                          actions.product(
                            row.entry,
                            $event.target.value,
                            row.record,
                          )
                        "
                      >
                        <option value="">No product / role</option>
                        <option
                          v-for="product in products"
                          :key="product.id"
                          :value="product.id"
                        >
                          {{ product.label }}
                        </option>
                      </select></label
                    >
                    <div class="shift-reference">
                      <label class="review-label"
                        >Job reference
                        <span class="review-muted">{{
                          row.entry.job_date_id
                            ? "· matched"
                            : "· confirmation needed"
                        }}</span>
                        <SearchableInput
                          :model-value="row.entry.job_date_query"
                          :auto-show-results="false"
                          :output="actions.jobOutput"
                          :url="
                            actions.jobUrl(run, row.entry.search_all_job_dates)
                          "
                          placeholder="Search reference name or number"
                          :clear-on-select="false"
                          :search-on-focus="true"
                          input-class="review-input"
                          results-class="!static !max-h-48"
                          @update:modelValue="
                            actions.jobQuery(row.entry, $event, row.record)
                          "
                          @selected="actions.job(row.entry, $event, row.record)"
                        />
                      </label>
                      <label class="review-check"
                        ><input
                          v-model="row.entry.search_all_job_dates"
                          type="checkbox"
                        />
                        Include older job dates</label
                      >
                      <div
                        v-if="!row.entry.job_date_id && suggestions(row).length"
                        class="review-suggestions"
                      >
                        <button
                          v-for="suggestion in suggestions(row)"
                          :key="suggestion.id"
                          type="button"
                          @click="
                            actions.job(row.entry, suggestion, row.record)
                          "
                        >
                          <strong>{{ actions.jobOutput(suggestion) }}</strong
                          ><span>{{
                            suggestion.reason || "Possible match"
                          }}</span
                          ><span
                            v-if="!suggestion.date_exact"
                            class="review-warning"
                            >Date differs — check the original</span
                          >
                        </button>
                      </div>
                    </div>
                  </div>
                  <template v-if="row.entry.payment_basis === 'hourly'">
                    <label class="review-label"
                      >Paid shift start<input
                        v-model="row.entry.work_start_time"
                        class="review-input"
                        type="time"
                    /></label>
                    <label class="review-label"
                      >Paid shift finish<input
                        v-model="row.entry.work_finish_time"
                        class="review-input"
                        type="time"
                    /></label>
                    <label class="review-label"
                      >Written hours<input
                        v-model="row.entry.claimed_hours"
                        class="review-input"
                        type="number"
                        min="0"
                        step="0.01"
                    /></label>
                    <div class="shift-calculated">
                      <span class="review-label">Paid shift duration</span>
                      <p
                        class="review-calculated"
                        :class="{
                          'review-warning': actions.hoursDiffer(row.entry),
                        }"
                      >
                        {{ actions.hours(actions.calculatedHours(row.entry))
                        }}<span v-if="actions.hoursDiffer(row.entry)">
                          · differs from written hours</span
                        >
                      </p>
                    </div>
                  </template>
                  <template v-else>
                    <label class="review-label"
                      >Quantity<input
                        v-model="row.entry.quantity"
                        class="review-input"
                        type="number"
                        min="0"
                        step="0.01"
                    /></label>
                    <label class="review-label"
                      >Unit rate<input
                        v-model="row.entry.unit_rate"
                        class="review-input"
                        type="number"
                        min="0"
                        step="0.01"
                    /></label>
                    <label class="review-label"
                      >Claim amount<input
                        v-model="row.entry.amount"
                        class="review-input"
                        type="number"
                        min="0"
                        step="0.01"
                    /></label>
                  </template>
                </div>
                <details
                  class="review-evidence"
                  :open="
                    row.entry.component_type !== 'work'
                  "
                >
                  <summary>
                    Supporting details ·
                    {{ row.entry.agreed_rate || "No rate" }} ·
                    {{ row.entry.vehicle_query || "No vehicle" }}
                  </summary>
                  <p class="review-muted">
                    Patient/job times describe work within the shift. Payroll is
                    calculated from the paid shift start and finish above.
                  </p>
                  <div class="review-grid mt-3">
                    <label class="review-label"
                      >Pay component<select
                        v-model="row.entry.component_type"
                        class="review-input"
                      >
                        <option value="work">Work</option>
                        <option value="outbound_driving">Drive out</option>
                        <option value="job_driving">Job driving</option>
                        <option value="return_driving">Drive back</option>
                      </select></label
                    >
                    <label class="review-label"
                      >Driving hours<input
                        v-model="row.entry.hours_driving"
                        class="review-input"
                        type="number"
                        min="0"
                        step="0.01"
                    /></label>
                    <label class="review-label"
                      >Vehicle <span class="review-muted">· optional</span
                      ><SearchableInput
                        :model-value="row.entry.vehicle_query"
                        :auto-show-results="false"
                        :output="actions.vehicleOutput"
                        url="/api/vehicle/search?query="
                        :clear-on-select="false"
                        input-class="review-input"
                        results-class="!static !max-h-48"
                        @update:modelValue="
                          actions.vehicleQuery(row.entry, $event, row.record)
                        "
                        @selected="
                          actions.vehicle(row.entry, $event, row.record)
                        "
                    /></label>
                    <label class="review-label"
                      >Co-worker <span class="review-muted">· optional</span
                      ><SearchableInput
                        :model-value="row.entry.co_worker_query"
                        :auto-show-results="false"
                        :output="actions.staffOutput"
                        url="/api/staff/search?query="
                        :clear-on-select="false"
                        input-class="review-input"
                        results-class="!static !max-h-48"
                        @update:modelValue="
                          actions.coWorkerQuery(row.entry, $event, row.record)
                        "
                        @selected="
                          actions.coWorker(row.entry, $event, row.record)
                        "
                    /></label>
                    <label class="review-label review-span"
                      >Notes<textarea
                        v-model="row.entry.notes"
                        class="review-input"
                        rows="2"
                      ></textarea>
                    </label>
                  </div>
                </details>
                <details
                  class="review-evidence"
                  :open="
                    childrenFor(row).some(
                      (child) =>
                        child.entry.vehicle_registration &&
                        !child.entry.vehicle_id,
                    )
                  "
                >
                  <summary>
                    Patient / event and journey evidence ({{
                      childrenFor(row).length + 1
                    }}
                    rows)
                  </summary>
                  <div
                    v-for="journey in [row, ...childrenFor(row)]"
                    :key="journey.key"
                    class="review-section"
                  >
                    <div class="review-grid">
                      <label v-if="journey !== row" class="review-label"
                        >Journey date<input
                          v-model="journey.entry.date"
                          class="review-input"
                          type="date"
                          @change="actions.date(journey.entry, journey.record)"
                      /></label>
                      <label class="review-label"
                        >Patient / event<input
                          v-model="journey.entry.patient_reference"
                          class="review-input"
                      /></label>
                      <label class="review-label"
                        >Category<input
                          v-model="journey.entry.patient_category"
                          class="review-input"
                      /></label>
                      <label class="review-label"
                        >Patient/job start<input
                          v-model="journey.entry.job_start_time"
                          class="review-input"
                          type="time"
                      /></label>
                      <label class="review-label"
                        >Patient/job finish<input
                          v-model="journey.entry.job_end_time"
                          class="review-input"
                          type="time"
                      /></label>
                      <label class="review-label"
                        >From postcode<input
                          v-model="journey.entry.job_start_postcode"
                          class="review-input"
                      /></label>
                      <label class="review-label"
                        >To postcode<input
                          v-model="journey.entry.job_end_postcode"
                          class="review-input"
                      /></label>
                      <template v-if="journey !== row">
                        <label class="review-label"
                          >Vehicle<SearchableInput
                            :model-value="journey.entry.vehicle_query"
                            :auto-show-results="false"
                            :output="actions.vehicleOutput"
                            url="/api/vehicle/search?query="
                            :clear-on-select="false"
                            input-class="review-input"
                            results-class="!static !max-h-48"
                            @update:modelValue="
                              actions.vehicleQuery(
                                journey.entry,
                                $event,
                                journey.record,
                              )
                            "
                            @selected="
                              actions.vehicle(
                                journey.entry,
                                $event,
                                journey.record,
                              )
                            "
                        /></label>
                        <label class="review-label"
                          >Notes<textarea
                            v-model="journey.entry.notes"
                            class="review-input"
                            rows="2"
                          ></textarea>
                        </label>
                      </template>
                    </div>
                    <button
                      v-if="selected.rows.length > 1 && !readOnly"
                      type="button"
                      class="review-link mt-2"
                      :disabled="busy"
                      @click="
                        actions.deleteEntry(journey.record, journey.entryIndex)
                      "
                    >
                      Remove row {{ journey.entry.sequence }}
                    </button>
                  </div>
                </details>
              </fieldset>
            </section>
          </div>
          <footer class="review-footer">
            <label v-if="needsAcknowledgement && !readOnly" class="review-check"
              ><input v-model="acknowledged" type="checkbox" /> I have checked
              the remaining warnings against the original. Payroll differences
              will still go through claim approval.</label
            >
            <p
              v-if="!readOnly && actions.blocker(selected.record)"
              class="review-warning"
            >
              {{ actions.blocker(selected.record) }}
            </p>
            <div class="review-heading">
              <span class="review-muted" role="status">{{
                readOnly ? actions.status(selected) : savedLabel
              }}</span>
              <div v-if="!readOnly" class="review-buttons">
                <button
                  type="button"
                  class="btn"
                  :disabled="busy"
                  @click="actions.save(selected.record)"
                >
                  {{ busy && !confirming ? "Saving…" : "Save progress" }}
                </button>
                <button
                  type="button"
                  class="btn accent"
                  :disabled="
                    busy ||
                    refreshingMatches ||
                    !!actions.blocker(selected.record) ||
                    (needsAcknowledgement && !acknowledged)
                  "
                  @click="confirmNext"
                >
                  {{ confirming ? "Importing…" : "Confirm and next" }}
                </button>
              </div>
            </div>
          </footer>
        </div>
        <aside
          v-if="sourceOpen && selected.record.source_view_url"
          class="review-source"
          aria-label="Original timesheet"
        >
          <a
            :href="selected.record.source_view_url"
            target="_blank"
            rel="noopener"
            class="review-link"
            >Open original in a new tab ↗</a
          >
          <iframe
            v-if="/\.pdf$/i.test(selected.record.source_file_name || '')"
            :src="selected.record.source_view_url"
            title="Original timesheet PDF"
          ></iframe>
          <template v-else-if="sourceIsImage">
            <div
              class="review-image-tools"
              aria-label="Image orientation controls"
            >
              <button
                type="button"
                title="Rotate left"
                aria-label="Rotate image left"
                @click="rotateSource(-90)"
              >
                ↶
              </button>
              <span>{{ sourceRotation }}°</span>
              <button
                type="button"
                title="Rotate right"
                aria-label="Rotate image right"
                @click="rotateSource(90)"
              >
                ↷
              </button>
              <button
                v-if="sourceRotation"
                type="button"
                @click="sourceRotation = 0"
              >
                Reset
              </button>
            </div>
            <div class="review-image-stage">
              <img
                :src="selected.record.source_view_url"
                alt="Original timesheet"
                :style="{ transform: `rotate(${sourceRotation}deg)` }"
              />
            </div>
          </template>
          <img
            v-else-if="sourceIsPreviewable"
            :src="selected.record.source_view_url"
            alt="Original timesheet"
          />
          <p v-else class="review-muted">
            Open this spreadsheet or document to compare it with the extracted
            rows.
          </p>
        </aside>
      </div>
    </section>
    <p v-else class="review-muted">No timesheets match this view.</p>
  </div>
</template>

<style scoped>
.timesheet-review {
  display: grid;
  grid-template-columns: 220px minmax(0, 1fr);
  gap: 24px;
  margin-top: 16px;
  min-width: 0;
  color: #1e293b;
}
.review-queue {
  min-width: 0;
  border-right: 1px solid #e2e8f0;
  padding-right: 16px;
}
.queue-items {
  display: grid;
  gap: 4px;
  max-height: 70vh;
  overflow-y: auto;
}
.queue-item {
  display: grid;
  gap: 3px;
  width: 100%;
  text-align: left;
  padding: 10px;
  border-radius: 8px;
  overflow-wrap: anywhere;
}
.queue-item[aria-current="true"] {
  background: #e0f2fe;
  color: #075985;
}
.queue-top,
.review-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-width: 0;
}
.queue-name {
  min-width: 0;
}
.queue-reviewers {
  display: inline-flex;
  flex-shrink: 0;
  padding-left: 5px;
}
.review-avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  margin-left: -5px;
  overflow: hidden;
  border: 2px solid #fff;
  border-radius: 999px;
  color: #fff;
  font-size: 8px;
  font-weight: 700;
  line-height: 1;
  box-shadow: 0 1px 2px rgb(15 23 42 / 20%);
}
.review-avatar-large {
  width: 27px;
  height: 27px;
  font-size: 9px;
}
.review-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.queue-name,
h3,
h4 {
  font-weight: 600;
}
h3 {
  font-size: 20px;
}
h4 {
  font-size: 15px;
}
.review-main,
.review-editor {
  min-width: 0;
}
.review-editor {
  container-type: inline-size;
}
.review-staff {
  max-width: 420px;
}
.shift-summary {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  text-align: left;
  padding: 8px 0;
  font-size: 13px;
}
.shift-job {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.shift-editor {
  min-width: 0;
}
.review-grid.shift-fields {
  grid-template-columns: 150px 120px 120px 110px minmax(120px, 1fr);
  gap: 10px;
}
.shift-matching {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 220px;
  gap: 12px;
  grid-column: 1 / -1;
  grid-row: 1;
}
.shift-reference {
  grid-column: 1;
  grid-row: 1;
  min-width: 0;
}
.shift-role {
  grid-column: 2;
  grid-row: 1;
}
.shift-date {
  grid-column: 1;
  grid-row: 2;
}
.shift-basis {
  grid-column: 1;
  grid-row: 3;
}
.shift-rate {
  grid-column: 2;
  grid-row: 3;
}
.shift-fields .review-calculated {
  padding: 10px 0 0;
  margin: 0;
}
@container (max-width: 700px) {
  .review-grid.shift-fields {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .shift-fields > * {
    grid-column: auto;
    grid-row: auto;
  }
  .shift-fields > .shift-matching {
    order: -2;
    grid-column: 1 / -1;
  }
  .shift-matching {
    grid-template-columns: minmax(0, 1fr);
  }
  .shift-reference,
  .shift-role {
    grid-column: 1 / -1;
    grid-row: auto;
  }
  .shift-reference {
    order: -1;
  }
  .shift-fields input[type="time"],
  .shift-fields input[type="number"] {
    max-width: 130px;
  }
  .shift-summary {
    flex-wrap: wrap;
    gap: 6px 12px;
  }
  .shift-job {
    flex-basis: 60%;
  }
}
.review-heading,
.review-buttons {
  text-transform: none;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
}
.review-buttons {
  justify-content: flex-start;
}
.review-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  flex: 0 0 34px;
  border: 1px solid #bae6fd;
  border-radius: 999px;
  color: #0369a1;
  background: #fff;
  font-size: 16px;
  font-weight: 700;
}
.review-icon svg {
  width: 17px;
  height: 17px;
}
.review-icon:hover,
.review-icon.is-active {
  background: #e0f2fe;
}
.review-menu summary,
.review-evidence summary {
  cursor: pointer;
  font-size: 13px;
}
.review-menu {
  max-width: 100%;
}
.review-muted {
  font-size: 12px;
  color: #64748b;
  margin: 5px 0;
}
.review-warning {
  color: #92400e;
  font-size: 13px;
}
.review-notice {
  background: #fffbeb;
  color: #92400e;
  border-radius: 8px;
  padding: 12px;
  margin: 16px 0;
  font-size: 14px;
}
.review-notice ul {
  padding-left: 20px;
  list-style: disc;
}
.review-content {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 20px;
}
.review-content.with-source {
  grid-template-columns: minmax(0, 1fr) minmax(240px, 40%);
}
.review-fields {
  min-width: 0;
}
.review-fields:disabled {
  opacity: 0.8;
}
.review-section {
  padding: 8px 0;
  border-bottom: 1px solid #e2e8f0;
}
.review-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
  margin-top: 12px;
}
.review-span {
  grid-column: 1 / -1;
  min-width: 0;
}
.review-label {
  text-transform: none;
  font-weight: 400;
  display: block;
  font-size: 13px;
  min-width: 0;
}
.review-input,
:deep(.review-input) {
  display: block;
  width: 100%;
  min-width: 0;
  min-height: 38px;
  margin-top: 5px;
  padding: 8px 10px;
  font-size: 14px;
  font-weight: 400;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  color: #0f172a;
  background: white;
}
.review-check {
  text-transform: none;
  font-weight: 400;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 13px;
  margin-top: 10px;
}
.review-check input {
  margin-top: 3px;
  flex-shrink: 0;
}
.review-calculated {
  padding: 14px 0;
  font-size: 14px;
}
.review-suggestions {
  display: grid;
  gap: 6px;
  margin-top: 10px;
}
.review-suggestions button {
  display: grid;
  gap: 4px;
  text-align: left;
  padding: 10px;
  border-radius: 6px;
  background: #f0f9ff;
  font-size: 13px;
  overflow-wrap: anywhere;
}
.review-suggestions span {
  font-size: 12px;
}
.review-evidence {
  margin-top: 16px;
}
.review-evidence p {
  font-size: 13px;
  margin: 8px 0;
}
.review-footer {
  position: sticky;
  bottom: 0;
  z-index: 20;
  padding: 8px 0;
  background: #fff;
  border-top: 1px solid #e2e8f0;
}
.review-footer .review-heading {
  margin-top: 12px;
}
.review-link {
  color: #0369a1;
  font-size: 13px;
  text-decoration: underline;
}
.review-source {
  min-width: 0;
  padding-top: 16px;
}
.review-source iframe {
  width: 100%;
  height: 650px;
  margin-top: 12px;
  border: 1px solid #cbd5e1;
}
.review-source img {
  width: 100%;
  height: auto;
  margin-top: 12px;
}
.review-image-tools {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin-top: 10px;
  font-size: 12px;
  color: #64748b;
}
.review-image-tools button {
  min-width: 32px;
  height: 32px;
  padding: 0 9px;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  background: #fff;
  color: #0f172a;
}
.review-image-stage {
  min-height: 360px;
  max-height: 70vh;
  margin-top: 8px;
  padding: 16px;
  overflow: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f1f5f9;
}
.review-image-stage img {
  margin: auto;
  transition: transform 150ms ease;
  transform-origin: center center;
}
@media (prefers-color-scheme: dark) {
  .review-footer {
    background: #0f172a;
    border-color: #334155;
  }
  .timesheet-review {
    color: #e2e8f0;
    color-scheme: dark;
  }
  .review-muted {
    color: #94a3b8;
  }
  .review-warning {
    color: #fcd34d;
  }
  .review-section,
  .review-queue {
    border-color: #334155;
  }
  .review-avatar {
    border-color: #0f172a;
  }
  .review-input,
  :deep(.review-input) {
    background: #0f172a;
    color: #e2e8f0;
    border-color: #475569;
    color-scheme: dark;
  }
  .queue-item[aria-current="true"],
  .review-suggestions button {
    background: #082f49;
    color: #bae6fd;
  }
  .review-notice {
    background: #422006;
    color: #fde68a;
  }
  .review-link {
    color: #7dd3fc;
  }
  .review-icon {
    color: #bae6fd;
    background: #0f172a;
    border-color: #0369a1;
  }
  .review-icon:hover,
  .review-icon.is-active {
    background: #082f49;
  }
  .review-input::placeholder,
  :deep(.review-input::placeholder) {
    color: #94a3b8;
  }
  .review-source iframe {
    border-color: #475569;
  }
  .review-image-tools {
    color: #94a3b8;
  }
  .review-image-tools button {
    color: #e2e8f0;
    background: #1e293b;
    border-color: #475569;
  }
  .review-image-stage {
    background: #1e293b;
  }
}
.queue-item:focus-visible,
.review-suggestions button:focus-visible,
.review-input:focus-visible,
:deep(.review-input:focus-visible) {
  outline: 2px solid #38bdf8;
  outline-offset: 2px;
}
@media (max-width: 1200px) {
  .review-content.with-source {
    grid-template-columns: minmax(0, 1fr);
  }
  .review-source {
    order: -1;
  }
  .review-source iframe {
    height: 420px;
  }
}
@media (max-width: 800px) {
  .timesheet-review {
    grid-template-columns: minmax(0, 1fr);
    gap: 12px;
  }
  .review-queue {
    border-right: 0;
    padding-right: 0;
  }
  .queue-items {
    max-height: 200px;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
@media (max-width: 480px) {
  .review-grid,
  .queue-items {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
